import { useCallback, useEffect, useState } from 'react';
import Soundfont from 'soundfont-player';

// Generic theory-audio API: play single notes, intervals, scales, chords,
// progressions. Built on top of soundfont-player which is already pulled
// in by the chord library page, so no new bundle cost.
//
// Usage:
//   const { play, ready, playing, error } = useTheoryAudio();
//   play({ type: 'interval', notes: ['C4', 'G4'], direction: 'ascending' });
//
// All names are scientific-pitch (C4, F#4, Bb3). Returns a Promise that
// resolves when playback ends.

const DEFAULT_SOUNDFONT = 'FluidR3_GM';
const DEFAULT_INSTRUMENT = 'acoustic_grand_piano';

// Allowed instrument names. The hook lets a caller (or an audio-example
// block via `instrument:`) override the default, but only with a known
// safe value to avoid CDN 404s.
const KNOWN_INSTRUMENTS = new Set([
  'acoustic_grand_piano',
  'electric_grand_piano',
  'bright_acoustic_piano',
  'acoustic_guitar_nylon',
  'acoustic_guitar_steel',
  'electric_guitar_clean',
  'electric_guitar_jazz',
  'electric_bass_finger',
  'acoustic_bass',
  'violin',
  'flute',
]);

let sharedContext = null;
const instrumentCache = new Map();

const getOrCreateContext = () => {
  if (!sharedContext) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) throw new Error('AudioContext non supportato dal browser');
    sharedContext = new Ctor();
  }
  return sharedContext;
};

const loadInstrument = (instrumentName) => {
  const key = `${DEFAULT_SOUNDFONT}::${instrumentName}`;
  if (!instrumentCache.has(key)) {
    const ctx = getOrCreateContext();
    instrumentCache.set(
      key,
      Soundfont.instrument(ctx, instrumentName, { soundfont: DEFAULT_SOUNDFONT, format: 'mp3' })
    );
  }
  return instrumentCache.get(key);
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Soundfont.play(name, when, opts) schedules in AudioContext time. To
// "await" a scheduled playback we compute the total scheduled duration
// and sleep accordingly.

// "Notes" can contain a rest token ("_" or "rest" or "-") which advances
// the time pointer without playing anything. Lets a lesson on pauses
// show a real silence with `notes: C4 _ C4 _`.
const isRest = (token) => {
  const t = String(token).trim().toLowerCase();
  return t === '_' || t === 'rest' || t === '-';
};

const playSequence = async (instrument, notes, noteDurationSec, gapSec = 0) => {
  const ctx = getOrCreateContext();
  let when = ctx.currentTime + 0.05;
  for (const note of notes) {
    if (!isRest(note)) {
      instrument.play(note, when, { duration: noteDurationSec });
    }
    when += noteDurationSec + gapSec;
  }
  const totalMs = (when - ctx.currentTime) * 1000;
  await sleep(totalMs);
};

const playChord = async (instrument, notes, durationSec) => {
  const ctx = getOrCreateContext();
  const when = ctx.currentTime + 0.05;
  notes.forEach((note) => {
    instrument.play(note, when, { duration: durationSec });
  });
  await sleep((when - ctx.currentTime + durationSec) * 1000);
};

export const useTheoryAudio = ({ instrument: instrumentName = DEFAULT_INSTRUMENT } = {}) => {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadInstrument(instrumentName)
      .then(() => { if (!cancelled) setReady(true); })
      .catch((err) => { if (!cancelled) setError(err); });
    return () => { cancelled = true; };
  }, [instrumentName]);

  const play = useCallback(async (spec) => {
    if (playing) return;
    setPlaying(true);
    try {
      const ctx = getOrCreateContext();
      if (ctx.state === 'suspended') await ctx.resume();
      // Per-spec instrument override (used by etudes that want
      // acoustic_guitar_nylon instead of the page's default piano).
      const requested = spec?.instrument;
      const effectiveInstrument = (requested && KNOWN_INSTRUMENTS.has(requested))
        ? requested
        : instrumentName;
      const instrument = await loadInstrument(effectiveInstrument);
      const type = spec.type || 'note';

      switch (type) {
        case 'note': {
          await playSequence(instrument, [spec.note], spec.duration || 1.5);
          break;
        }

        case 'interval': {
          const noteDur = spec.noteDuration || 0.75;
          const gap = spec.gap || 0.05;
          const direction = spec.direction || 'ascending';
          if (direction === 'harmonic') {
            await playChord(instrument, spec.notes, noteDur * 2);
          } else if (direction === 'descending') {
            await playSequence(instrument, [...spec.notes].reverse(), noteDur, gap);
          } else if (direction === 'both') {
            await playSequence(instrument, spec.notes, noteDur, gap);
            await sleep(200);
            await playChord(instrument, spec.notes, noteDur * 1.5);
          } else {
            await playSequence(instrument, spec.notes, noteDur, gap);
          }
          break;
        }

        case 'scale': {
          const noteDur = spec.noteDuration || 0.4;
          const ascend = spec.notes;
          const descend = [...spec.notes].slice(0, -1).reverse();
          const all = ascend.concat(descend);
          await playSequence(instrument, all, noteDur, 0.02);
          break;
        }

        case 'sequence': {
          // Melodic line played once in written order (no descending
          // return). Unlike 'scale', the notes are not reversed - used
          // by lessons that show a phrase, a tone row, a raga aroha, etc.
          const noteDur = spec.noteDuration || 0.45;
          await playSequence(instrument, spec.notes, noteDur, 0.02);
          break;
        }

        case 'chord': {
          const duration = spec.duration || 2;
          await playChord(instrument, spec.notes, duration);
          break;
        }

        case 'polyphony': {
          // Schedule arbitrary timed events: [{ note, start, duration }]
          // where `start` and `duration` are in seconds. Multiple notes
          // can overlap (true polyphony) - the bass attacks once and
          // rings while the treble plays its own line above.
          const startTime = ctx.currentTime + 0.05;
          let maxEnd = 0;
          for (const evt of spec.events) {
            if (!evt || !evt.note) continue;
            instrument.play(evt.note, startTime + evt.start, { duration: evt.duration });
            maxEnd = Math.max(maxEnd, evt.start + evt.duration);
          }
          await sleep(maxEnd * 1000 + 200);
          break;
        }

        case 'progression': {
          const bpm = spec.bpm || 80;
          const beatsPerChord = spec.beatsPerChord || 4;
          const chordDur = (60 / bpm) * beatsPerChord;
          for (const chord of spec.chords) {
            await playChord(instrument, chord, chordDur);
          }
          break;
        }

        default:
          throw new Error(`Tipo audio sconosciuto: ${type}`);
      }
    } catch (err) {
      console.error('TheoryAudio play error:', err);
      setError(err);
    } finally {
      setPlaying(false);
    }
  }, [instrumentName, playing]);

  return { play, ready, playing, error };
};

export default useTheoryAudio;
